import * as c from "./config";
import CustomStorage from "./lib/CustomStorage";
import Database from "./lib/database";
import { isBrowser } from "./lib/utils";
import {
  AddClothFilePayload,
  AddClothPayload,
  AddMaterialFilePayload,
  AddMaterialPayload,
  AddSkullFilePayload,
  AddSkullPayload,
  Body,
  BodyAddFilePayload,
  BodyAddPayload,
  BodyUpdateFilePayload,
  BodyUpdatePayload,
  Cb,
  Cloth,
  Config,
  DBRes,
  ErrorRes,
  Exchange,
  GetClothsOptions,
  GetMaterialsOptions,
  LocalRegister,
  LocalRegisterPayload,
  LoginPayload,
  Material,
  Profile,
  RefreshTokenPayload,
  RefToken,
  SignedUrl,
  Skull,
  SkullMaterial,
  SkullMaterialAddFilePayload,
  SkullMaterialAddPayload,
  SkullMaterialUpdateFilePayload,
  SkullMaterialUpdatePayload,
  SuccessRes,
  UpdateClothPayload,
  UpdateMaterialPayload,
  UpdateProfilePayload,
  UpdateProfilePicture,
  UpdateProfilePicturePayload,
  UpdateSkullPayload,
  Verify,
  VerifyOtpPayload,
} from "./types";

export type * from "./types";

export { default as CustomStorage } from "./lib/CustomStorage";

export default class SSO {
  readonly serviceType = "AVATAR_SSO";
  protected readonly db: Database | CustomStorage;
  protected ssoClientUrl = c.ssoClientUrl;
  protected ssoServerUrl = c.ssoServerUrl;
  readonly sdkKey: string;
  readonly dbId = "local-user";
  private callbacks = new Set<Cb>();
  private cb: Cb = (payload) => {
    for (const cb of this.callbacks) cb(payload);
  };

  constructor(private readonly config: Config) {
    this.sdkKey = config.sdkKey ?? "no-sdk-key";
    this.db = config.storage instanceof CustomStorage ? config.storage : c.db;
    this.ssoClientUrl = config.clientUrl ?? c.ssoClientUrl;
    this.ssoServerUrl = config.serverUrl ?? c.ssoServerUrl;
  }

  readonly register = async (
    payload: LocalRegisterPayload,
  ): Promise<LocalRegister> => {
    const { redirectUri, type, ...body } = payload;
    const queryParams = this.emailVerificationQuery({ redirectUri, type });

    const res = await fetch(`${this.ssoServerUrl}/users/signup${queryParams}`, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        "Content-Type": "application/json",
        "x-sdk-key": this.sdkKey,
      },
    });
    const json = (await res.json().catch(() => null)) as
      | SuccessRes<LocalRegister>
      | ErrorRes;

    if (!res?.ok || !json?.success)
      throw new Error(json?.message ?? "SSO-SDK: Local register failed.");

    this.cb({
      type: c.actions.local_register,
      payload: json.data,
    });

    return json.data;
  };

  private readonly localLogin = async (payload: LoginPayload) => {
    if (!(payload instanceof Object)) return;

    const { redirectUri, type, ...body } = payload;
    const queryParams = this.emailVerificationQuery({ redirectUri, type });

    const res = await fetch(`${this.ssoServerUrl}/users/signin${queryParams}`, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        "Content-Type": "application/json",
        "x-sdk-key": this.sdkKey,
      },
    });

    const json: SuccessRes | ErrorRes = await res.json().catch(() => null);

    if (!res?.ok || !json?.success)
      throw new Error(json?.message ?? "SSO-SDK: Local login failed.");

    this.cb({
      type: c.actions.local_login,
      payload: null,
    });
  };
  private readonly ssoLogin = async (redirectUrl: LoginPayload) => {
    if (redirectUrl instanceof Object) return;

    this.throwIfNotBrowser();

    const r = `${this.ssoClientUrl}/sso?sdkKey=${this.sdkKey}&redirectUri=${redirectUrl}`;

    window.location.href = r;
  };
  /**
   * Login function that triggers both local and SSO login flows. If the payload is a string, it will trigger the SSO login flow with the payload as the redirect URL. If the payload is an object, it will trigger the local login flow with the payload as the login data.
   * @param payload just redirect URL string for SSO login or LocalLoginPayload for local login
   * @returns
   */
  readonly login = async (payload: LoginPayload) => {
    await Promise.all([this.localLogin(payload), this.ssoLogin(payload)]);
  };

  private readonly decodeJwtPayload = <T>(token: string) => {
    try {
      const payload = token.split(".")[1];
      if (!payload) return null;

      const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");

      return JSON.parse(atob(base64)) as T;
    } catch {
      return null;
    }
  };

  /**
   * - it throws.
   */
  private readonly autoRefresh = async () => {
    const existingUser = await this.getLocalUser().catch(() => null);

    if (!existingUser) return null;

    const { accessToken, refreshToken } = existingUser.tokens;

    const decodedRT = this.decodeJwtPayload<RefreshTokenPayload>(refreshToken);
    const decodedAT = this.decodeJwtPayload<RefreshTokenPayload>(accessToken);

    const now = Date.now();

    const isRefreshTokenValid =
      decodedRT?.exp != null && now < decodedRT.exp * 1000;

    if (!isRefreshTokenValid) {
      await this.db.reset();
      throw new Error(`SSO-SDK: Session cleared. Please login again.`);
    }

    // refresh if the access token is undecodable or expires within 5 minutes
    const refreshMarginMs = 5 * 60 * 1000;
    const shouldRefresh =
      decodedAT?.exp == null || now + refreshMarginMs > decodedAT.exp * 1000;

    if (!shouldRefresh) return existingUser.tokens;

    return await this.refresh();
  };

  private readonly localExchange = async (
    email: string | null,
    token: string | null,
  ): Promise<Exchange | void> => {
    if (!email || !token) return;

    const res = await fetch(`${this.ssoServerUrl}/users/verify`, {
      method: "POST",
      body: JSON.stringify({
        token,
        email,
      }),
      headers: {
        "Content-Type": "application/json",
        "x-sdk-key": this.sdkKey,
      },
    });

    const verifyRes: SuccessRes<Verify> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res.ok || !verifyRes?.success)
      throw new Error(verifyRes?.message ?? `SSO-SDK: Local exchange failed.`);

    const profileRes = await fetch(`${this.ssoServerUrl}/users/profile`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${verifyRes.data.tokens.accessToken}`,
        "x-sdk-key": this.sdkKey,
      },
    });

    const profileJson: SuccessRes<Profile> | ErrorRes = await profileRes
      .json()
      .catch(() => null);

    if (!profileRes.ok || !profileJson?.success)
      throw new Error(profileJson?.message ?? `SSO-SDK: Profile fetch failed.`);

    await this.db.delete(this.dbId);

    await this.db.create({
      ...profileJson.data,
      userId: profileJson.data.id,
      tokens: verifyRes.data.tokens,
      id: this.dbId,
    });

    this.cb({
      type: c.actions.local_exchange,
      payload: profileJson.data,
    });

    return { ...profileJson.data, tokens: verifyRes.data.tokens };
  };

  readonly verifyOtp = async (payload: VerifyOtpPayload) => {
    await this.db.reset();

    const email = payload.email.trim().toLowerCase();
    const otpCode = payload.otpCode.trim();

    if (!email || !otpCode) throw new Error(`SSO-SDK: Invalid OTP payload.`);

    const res = await this.localExchange(email, otpCode);

    if (!res) throw new Error(`SSO-SDK: OTP verification failed.`);

    return res;
  };
  private readonly ssoExchange = async (
    email: string | null,
    token: string | null,
  ): Promise<Exchange | void> => {
    if (email || !token) return;

    const res = await fetch(`${this.ssoServerUrl}/sso/exchange`, {
      method: "POST",
      body: JSON.stringify({
        token,
        sdkKey: this.sdkKey,
      }),
      headers: {
        "Content-Type": "application/json",
        "x-sdk-key": this.sdkKey,
      },
    });

    const data: SuccessRes<{
      tokens: {
        accessToken: string;
        refreshToken: string;
      };
    }> = await res.json().catch(() => null);

    if (!res.ok || !data?.success)
      throw new Error(data?.message ?? `SSO-SDK: SSO exchange failed.`);

    const profileRes = await fetch(`${this.ssoServerUrl}/users/profile`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${data.data.tokens.accessToken}`,
        "x-sdk-key": this.sdkKey,
      },
    });

    const profileJson: SuccessRes<Profile> | ErrorRes = await profileRes
      .json()
      .catch(() => null);

    if (!profileRes.ok || !profileJson?.success)
      throw new Error(profileJson?.message ?? `SSO-SDK: Profile fetch failed.`);

    await this.db.open();

    await this.db.delete(this.dbId);

    await this.db.create({
      ...profileJson.data,
      userId: profileJson.data.id,
      tokens: data.data.tokens,
      id: this.dbId,
    });

    this.cb({
      type: c.actions.sso_exchange,
      payload: profileJson.data,
    });

    return { ...profileJson.data, tokens: data.data.tokens };
  };
  /**
   * - this will just skip the exchange returning null if the query params are not correct and won't throw an error.
   */
  readonly exchange = async () => {
    this.throwIfNotBrowser();

    const url = new URL(window.location.href);
    const email = url.searchParams.get("email");
    const token = url.searchParams.get("token");

    const isExchanging = !!(email && token);

    if (isExchanging) {
      url.searchParams.delete("email");
      url.searchParams.delete("token");
      window.history.replaceState({}, "", url);
    }

    if (!isExchanging) {
      await this.autoRefresh().catch((e) => {
        if (/session cleared/i.test(e?.message ?? "")) {
          this.cb({ type: c.actions.session_cleared, payload: null });
        }
        throw new Error(e?.message ?? `SSO-SDK: Auto refresh failed.`);
      });
      return null;
    }

    const sanitizedEmail = email
      ? email.trim().toLowerCase().replace(/\s+/g, "+")
      : null;

    const res = await Promise.all([
      this.localExchange(sanitizedEmail, token),
      this.ssoExchange(sanitizedEmail, token),
    ]).then((r) => r.find((r2) => !!r2));

    if (!res) throw new Error(`SSO-SDK: Exchange failed.`);

    return res;
  };

  readonly fetchProfile = async () => {
    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/users/profile`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const pRes: SuccessRes<Profile> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res?.ok || !pRes?.success)
      throw new Error(pRes?.message ?? `SSO-SDK: Profile fetch failed.`);

    this.cb({
      type: c.actions.profile_fetch,
      payload: pRes.data,
    });

    return pRes.data;
  };

  // dedupes concurrent refresh calls so a rotated refresh token isn't reused
  private refreshPromise: Promise<RefToken> | null = null;
  readonly refresh = async () => {
    this.refreshPromise ??= this.doRefresh().finally(() => {
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  };

  private readonly doRefresh = async () => {
    const dbUser = await this.db.getById<DBRes | undefined | null>(this.dbId);

    if (!dbUser) throw new Error(`SSO-SDK: No user found in IndexedDB.`);

    const res = await fetch(`${this.ssoServerUrl}/users/refresh`, {
      method: "POST",
      body: JSON.stringify({
        token: dbUser.tokens.refreshToken,
      }),
      headers: {
        "Content-Type": "application/json",
        "x-sdk-key": this.sdkKey,
      },
    });

    const refTokeRes: SuccessRes<RefToken> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res.ok || !refTokeRes?.success) {
      await this.db.delete(this.dbId);
      this.cb({
        type: c.actions.session_cleared,
        payload: null,
      });
      throw new Error(refTokeRes?.message ?? `SSO-SDK: Refresh failed.`);
    }

    await this.db.update(this.dbId, {
      ...dbUser,
      tokens: {
        accessToken: refTokeRes.data.accessToken,
        refreshToken: refTokeRes.data.refreshToken,
      },
    });

    this.cb({
      type: c.actions.refresh,
      payload: refTokeRes.data,
    });

    return refTokeRes.data;
  };

  readonly logout = async (): Promise<Boolean> => {
    try {
      const res = await this.withRefresh(async (aT) => {
        return await fetch(`${this.ssoServerUrl}/users/logout`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${aT}`,
            "x-sdk-key": this.sdkKey,
          },
        });
      });

      const json: SuccessRes | ErrorRes = await res.json().catch(() => null);

      if (!res?.ok || !json?.success)
        console.log(json?.message ?? `SSO-SDK: Local logout failed.`);

      await this.db.delete(this.dbId);

      this.cb({
        type: c.actions.local_logout,
        payload: null,
      });

      this.cb({
        type: c.actions.session_cleared,
        payload: null,
      });
    } catch (e) {
      console.warn(
        e instanceof Error ? e.message : `SSO-SDK: Server logout failed.`,
      );
      await this.db.delete(this.dbId);
      this.cb({
        type: c.actions.session_cleared,
        payload: null,
      });
    }

    return true;
  };

  readonly onEvents = (cb: Cb) => {
    this.callbacks.add(cb);
    return () => this.callbacks.delete(cb);
  };

  protected readonly withRefresh = async (
    api: (aT: string) => Promise<Response>,
  ) => {
    await this.db.open();
    const dbUser = (await this.db.getById(this.dbId)) as DBRes | undefined;

    if (!dbUser) throw new Error(`SSO-SDK: No user found in IndexedDB.`);

    const r = await api(dbUser.tokens.accessToken);

    if (r.status !== 401) return r;

    const resToken = await this.refresh();

    const nR = await api(resToken.accessToken);

    return nR;
  };

  readonly profileUpdate = async (payload: UpdateProfilePayload) => {
    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/users/profile`, {
        method: "PATCH",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const pRes: SuccessRes<Profile> | ErrorRes = await res
      .json()
      .catch(() => null);
    if (!res?.ok || !pRes?.success)
      throw new Error(pRes?.message ?? `SSO-SDK: Profile update failed.`);

    const { id, ...rest } = pRes.data;

    await this.db.update(this.dbId, {
      ...rest,
      userId: id,
    });

    this.cb({
      type: c.actions.profile_update,
      payload: pRes.data,
    });

    return pRes.data;
  };

  readonly avatarUpdate = async (file: File) => {
    const photoURL = await this.fileUpload(file);

    if (!photoURL) throw new Error(`SSO-SDK: File upload failed.`);

    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/users/profile/avatar`, {
        method: "PUT",
        body: JSON.stringify({
          photoURL,
        } satisfies UpdateProfilePicturePayload),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const pRes: SuccessRes<UpdateProfilePicture> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res?.ok || !pRes?.success)
      throw new Error(pRes?.message ?? `SSO-SDK: Profile update failed.`);

    const { id, ...rest } = pRes.data;

    await this.db.update(this.dbId, {
      ...rest,
      userId: id,
    });

    this.cb({
      type: c.actions.profile_photo_update,
      payload: pRes.data,
    });

    return pRes.data;
  };

  private readonly fileUpload = async (file: File) => {
    const { type } = file;

    const url = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/uploads/url?fileType=${type}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const json: SuccessRes<SignedUrl> | ErrorRes = await url
      .json()
      .catch(() => null);
    if (!url?.ok || !json?.success)
      throw new Error(json?.message ?? `SSO-SDK: could not get upload url.`);

    const upload = await fetch(json.data.url, {
      method: json.data.method,
      body: file,
      headers: json.data.headers,
    });

    if (!upload.ok) throw new Error(`SSO-SDK: could not upload file.`);

    const { origin, pathname } = new URL(json.data.url);

    return `${origin}${pathname}`;
  };

  private readonly clothAdd = async (payload: AddClothFilePayload) => {
    const thumb = await this.fileUpload(payload.thumb);
    if (!thumb) throw new Error(`SSO-SDK: File upload failed.`);

    const jsonPayload = {
      ...payload,
      thumb,
    } satisfies AddClothPayload;

    await this.db.open();

    const dbUser = (await this.db.getById(this.dbId)) as DBRes | undefined;

    if (!dbUser) throw new Error(`SSO-SDK: No user found in IndexedDB.`);

    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/clothings`, {
        method: "POST",
        body: JSON.stringify(jsonPayload),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT || dbUser.tokens.accessToken}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const bodyRes: SuccessRes<Cloth> | ErrorRes = await res
      .json()
      .catch(() => null);
    if (!res?.ok || !bodyRes?.success)
      throw new Error(bodyRes?.message ?? `SSO-SDK: Cloth add failed.`);

    const { data } = bodyRes;

    this.cb({
      type: c.actions.cloth_add,
      payload: data,
    });

    return data;
  };

  readonly clothGetAll = async (options?: GetClothsOptions) => {
    const queryParams = new URLSearchParams({
      skip: Math.ceil(options?.skip ?? 0).toString(),
      limit: Math.ceil(options?.limit ?? 10).toString(),
      sort: options?.sort ?? "asc",
    });

    const res = await this.withRefresh(async (aT) => {
      return await fetch(
        `${this.ssoServerUrl}/clothings?${queryParams.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${aT}`,
            "x-sdk-key": this.sdkKey,
          },
        },
      );
    });

    const clothRes: SuccessRes<Cloth[]> | ErrorRes = await res
      .json()
      .catch(() => null);
    if (!res?.ok || !clothRes?.success)
      throw new Error(clothRes?.message ?? `SSO-SDK: Get all cloths failed.`);

    this.cb({
      type: c.actions.cloth_fetch_all,
      payload: clothRes.data,
    });

    return clothRes.data;
  };

  readonly clothGetById = async (clothId: string) => {
    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/clothings/${clothId}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const clothRes: SuccessRes<Cloth> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res?.ok || !clothRes?.success)
      throw new Error(clothRes?.message ?? `SSO-SDK: Get cloth by id failed.`);

    this.cb({
      type: c.actions.cloth_fetch,
      payload: clothRes.data,
    });

    return clothRes.data;
  };

  private readonly clothUpdate = async (
    clothId: string,
    payload: UpdateClothPayload,
  ) => {
    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/clothings/${clothId}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const clothRes: SuccessRes<Cloth> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res?.ok || !clothRes?.success)
      throw new Error(clothRes?.message ?? `SSO-SDK: Cloth update failed.`);

    const { data } = clothRes;

    this.cb({
      type: c.actions.cloth_update,
      payload: data,
    });

    return data;
  };

  private readonly clothDelete = async (clothId: string) => {
    await this.db.open();

    const dbUser = (await this.db.getById(this.dbId)) as DBRes | undefined;

    if (!dbUser) throw new Error(`SSO-SDK: No user found in IndexedDB.`);

    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/clothings/${clothId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT || dbUser.tokens.accessToken}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const clothRes: SuccessRes<Cloth> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res?.ok || !clothRes?.success)
      throw new Error(clothRes?.message ?? `SSO-SDK: Cloth delete failed.`);

    this.cb({
      type: c.actions.cloth_delete,
      payload: clothRes.data,
    });

    return clothRes.data;
  };

  // materials
  private readonly materialAdd = async (payload: AddMaterialFilePayload) => {
    const thumb = await this.fileUpload(payload.thumb);
    if (!thumb) throw new Error(`SSO-SDK: File upload failed.`);

    const jsonPayload = {
      ...payload,
      thumb: thumb,
    } satisfies AddMaterialPayload;

    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/clothings/materials`, {
        method: "POST",
        body: JSON.stringify(jsonPayload),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const materialRes: SuccessRes<Material> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res?.ok || !materialRes?.success)
      throw new Error(materialRes?.message ?? `SSO-SDK: Material add failed.`);

    const { data } = materialRes;

    this.cb({
      type: c.actions.material_add,
      payload: data,
    });

    return data;
  };

  readonly materialGetAll = async (options?: GetMaterialsOptions) => {
    const params = new URLSearchParams({
      skip: options?.skip?.toString() || "0",
      limit: options?.limit?.toString() || "10",
      sort: options?.sort || "asc",
    });

    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/clothings/materials?${params}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const materialRes: SuccessRes<Material[]> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res?.ok || !materialRes?.success)
      throw new Error(
        materialRes?.message ?? `SSO-SDK: Material fetch all failed.`,
      );

    const { data } = materialRes;

    this.cb({
      type: c.actions.material_fetch_all,
      payload: data,
    });

    return data;
  };

  readonly materialGetById = async (materialId: string) => {
    const res = await this.withRefresh(async (aT) => {
      return await fetch(
        `${this.ssoServerUrl}/clothings/materials/${materialId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${aT}`,
            "x-sdk-key": this.sdkKey,
          },
        },
      );
    });

    const materialRes: SuccessRes<Material> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res?.ok || !materialRes?.success)
      throw new Error(
        materialRes?.message ?? `SSO-SDK: Material fetch by id failed.`,
      );

    const { data } = materialRes;

    this.cb({
      type: c.actions.material_fetch,
      payload: data,
    });

    return data;
  };

  private readonly materialUpdate = async (
    materialId: string,
    payload: UpdateMaterialPayload,
  ) => {
    const res = await this.withRefresh(async (aT) => {
      return await fetch(
        `${this.ssoServerUrl}/clothings/materials/${materialId}`,
        {
          method: "PATCH",
          body: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${aT}`,
            "x-sdk-key": this.sdkKey,
          },
        },
      );
    });

    const materialRes: SuccessRes<Material> | ErrorRes = await res
      .json()
      .catch(() => null);
    if (!res?.ok || !materialRes?.success)
      throw new Error(
        materialRes?.message ?? `SSO-SDK: Material update failed.`,
      );

    const { data } = materialRes;

    this.cb({
      type: c.actions.material_update,
      payload: data,
    });

    return data;
  };

  private readonly materialDelete = async (materialId: string) => {
    const res = await this.withRefresh(async (aT) => {
      return await fetch(
        `${this.ssoServerUrl}/clothings/materials/${materialId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${aT}`,
            "x-sdk-key": this.sdkKey,
          },
        },
      );
    });

    const materialRes: SuccessRes<Material> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res?.ok || !materialRes?.success)
      throw new Error(
        materialRes?.message ?? `SSO-SDK: Material delete failed.`,
      );

    const { data } = materialRes;

    this.cb({
      type: c.actions.material_delete,
      payload: data,
    });

    return data;
  };

  // skulls
  private readonly skullAdd = async (payload: AddSkullFilePayload) => {
    const preview = await this.fileUpload(payload.filePreview);
    const model = await this.fileUpload(payload.file3d);

    if (!preview || !model) throw new Error(`SSO-SDK: File upload failed.`);

    const jsonPayload = {
      ...payload,
      previewUrl: preview,
      url3d: model,
    } satisfies AddSkullPayload;

    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/skulls`, {
        method: "POST",
        body: JSON.stringify(jsonPayload),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const skullRes: SuccessRes<Skull> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res?.ok || !skullRes?.success)
      throw new Error(skullRes?.message ?? "SSO-SDK: Skull add failed.");

    const { data } = skullRes;

    this.cb({
      type: c.actions.skull_add,
      payload: data,
    });

    return data;
  };

  private readonly skullUpdate = async (
    skullId: string,
    payload: UpdateSkullPayload,
  ) => {
    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/skulls/${skullId}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const skullRes = (await res.json().catch(() => null)) as
      | SuccessRes<Skull>
      | ErrorRes;

    if (!res?.ok || !skullRes?.success)
      throw new Error(skullRes?.message ?? "SSO-SDK: Skull update failed.");

    const { data } = skullRes;

    this.cb({
      type: c.actions.skull_update,
      payload: data,
    });

    return data;
  };

  readonly skullGetAll = async () => {
    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/skulls`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const skullRes: SuccessRes<Skull[]> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res?.ok || !skullRes?.success)
      throw new Error(skullRes?.message ?? "SSO-SDK: Skull get all failed.");

    const { data } = skullRes;

    this.cb({
      type: c.actions.skull_fetch_all,
      payload: data,
    });

    return data;
  };

  readonly skullGetById = async (skullId: string) => {
    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/skulls/${skullId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const skullRes: SuccessRes<Skull> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res?.ok || !skullRes?.success)
      throw new Error(skullRes?.message ?? "SSO-SDK: Skull get by id failed.");

    const { data } = skullRes;

    this.cb({
      type: c.actions.skull_fetch,
      payload: data,
    });

    return data;
  };

  private readonly skullDelete = async (skullId: string) => {
    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/skulls/${skullId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const skullRes: SuccessRes<Skull> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res?.ok || !skullRes?.success)
      throw new Error(skullRes?.message ?? "SSO-SDK: Skull delete failed.");

    const { data } = skullRes;

    this.cb({
      type: c.actions.skull_delete,
      payload: data,
    });

    return data;
  };

  // skull material
  private readonly skullMaterialAdd = async (
    payload: SkullMaterialAddFilePayload,
  ) => {
    const { file3d, previewFile, ...rest } = payload;
    const preview = await this.fileUpload(previewFile);
    const model = await this.fileUpload(file3d);

    if (!preview || !model) throw new Error(`SSO-SDK: File upload failed.`);

    const jsonPayload = {
      ...rest,
      previewUrl: preview,
      url3d: model,
    } satisfies SkullMaterialAddPayload;

    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/skulls/materials`, {
        method: "POST",
        body: JSON.stringify(jsonPayload),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const skullRes: SuccessRes<SkullMaterial> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res?.ok || !skullRes?.success)
      throw new Error(
        skullRes?.message ?? "SSO-SDK: Skull material add failed.",
      );

    const { data } = skullRes;

    this.cb({
      type: c.actions.skull_material_add,
      payload: data,
    });

    return data;
  };

  readonly skullMaterialGetAll = async () => {
    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/skulls/materials`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const skullRes: SuccessRes<SkullMaterial[]> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res?.ok || !skullRes?.success)
      throw new Error(
        skullRes?.message ?? "SSO-SDK: Skull materials get all failed.",
      );

    const { data } = skullRes;

    this.cb({
      type: c.actions.skull_material_fetch_all,
      payload: data,
    });

    return data;
  };

  readonly skullMaterialGetById = async (id: string) => {
    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/skulls/materials/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const skullRes: SuccessRes<SkullMaterial> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res?.ok || !skullRes?.success)
      throw new Error(
        skullRes?.message ?? "SSO-SDK: Skull material get by id failed.",
      );

    const { data } = skullRes;

    this.cb({
      type: c.actions.skull_material_fetch,
      payload: data,
    });

    return data;
  };

  private readonly skullMaterialUpdate = async (
    id: string,
    payload: SkullMaterialUpdateFilePayload,
  ) => {
    const { file3d, previewFile, ...rest } = payload;
    const preview = previewFile && (await this.fileUpload(previewFile));
    const model = file3d && (await this.fileUpload(file3d));

    const jsonPayload = {
      ...rest,
      previewUrl: preview,
      url3d: model,
    } satisfies SkullMaterialUpdatePayload;

    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/skulls/materials/${id}`, {
        method: "PATCH",
        body: JSON.stringify(jsonPayload),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const skullRes: SuccessRes<SkullMaterial> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res?.ok || !skullRes?.success)
      throw new Error(
        skullRes?.message ?? "SSO-SDK: Skull material update failed.",
      );

    const { data } = skullRes;

    this.cb({
      type: c.actions.skull_material_update,
      payload: data,
    });

    return data;
  };

  private readonly skullMaterialDelete = async (id: string) => {
    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/skulls/materials/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const skullRes: SuccessRes<SkullMaterial> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res?.ok || !skullRes?.success)
      throw new Error(
        skullRes?.message ?? "SSO-SDK: Skull material delete failed.",
      );

    const { data } = skullRes;

    this.cb({
      type: c.actions.skull_material_delete,
      payload: data,
    });

    return data;
  };

  // body
  private readonly bodyAdd = async (payload: BodyAddFilePayload) => {
    const { file3d, previewFile, ...rest } = payload;
    const preview = await this.fileUpload(previewFile);
    const model = await this.fileUpload(file3d);

    if (!preview || !model) throw new Error(`SSO-SDK: File upload failed.`);

    const jsonPayload = {
      ...rest,
      previewUrl: preview,
      url3d: model,
    } satisfies BodyAddPayload;

    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/bodies`, {
        method: "POST",
        body: JSON.stringify(jsonPayload),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const bodyRes: SuccessRes<Body> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res?.ok || !bodyRes?.success)
      throw new Error(bodyRes?.message ?? "SSO-SDK: Body add failed.");

    const { data } = bodyRes;

    this.cb({
      type: c.actions.body_add,
      payload: data,
    });

    return data;
  };

  readonly bodyGetAll = async () => {
    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/bodies`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const bodyRes: SuccessRes<Body[]> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res?.ok || !bodyRes?.success)
      throw new Error(bodyRes?.message ?? "SSO-SDK: Body add failed.");

    const { data } = bodyRes;

    this.cb({
      type: c.actions.body_fetch_all,
      payload: data,
    });

    return data;
  };

  readonly bodyGetById = async (id: string) => {
    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/bodies/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const bodyRes: SuccessRes<Body> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res?.ok || !bodyRes?.success)
      throw new Error(bodyRes?.message ?? "SSO-SDK: Body add failed.");

    const { data } = bodyRes;

    this.cb({
      type: c.actions.body_fetch,
      payload: data,
    });

    return data;
  };

  private readonly bodyUpdate = async (
    id: string,
    payload: BodyUpdateFilePayload,
  ) => {
    const { file3d, previewFile, ...rest } = payload;
    const preview = previewFile && (await this.fileUpload(previewFile));
    const model = file3d && (await this.fileUpload(file3d));

    const jsonPayload = {
      ...rest,
      previewUrl: preview,
      url3d: model,
    } satisfies BodyUpdatePayload;

    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/bodies/${id}`, {
        method: "PATCH",
        body: JSON.stringify(jsonPayload),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const bodyRes: SuccessRes<Body> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res?.ok || !bodyRes?.success)
      throw new Error(bodyRes?.message ?? "SSO-SDK: Body add failed.");

    const { data } = bodyRes;

    this.cb({
      type: c.actions.body_update,
      payload: data,
    });

    return data;
  };

  private readonly bodyDelete = async (id: string) => {
    const res = await this.withRefresh(async (aT) => {
      return await fetch(`${this.ssoServerUrl}/bodies/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${aT}`,
          "x-sdk-key": this.sdkKey,
        },
      });
    });

    const bodyRes: SuccessRes<Body> | ErrorRes = await res
      .json()
      .catch(() => null);

    if (!res?.ok || !bodyRes?.success)
      throw new Error(bodyRes?.message ?? "SSO-SDK: Body add failed.");

    const { data } = bodyRes;

    this.cb({
      type: c.actions.body_delete,
      payload: data,
    });

    return data;
  };

  private validatedUrl = (url: string) => {
    try {
      return new URL(url).href;
    } catch {
      throw new Error(`SSO-SDK: Invalid URL.`);
    }
  };

  private emailVerificationQuery = ({
    redirectUri,
    type,
  }: {
    redirectUri?: string;
    type?: "otp";
  }) => {
    const params = new URLSearchParams();

    if (redirectUri) params.set("redirectUri", this.validatedUrl(redirectUri));
    if (type) params.set("type", type);

    const query = params.toString();

    return query ? `?${query}` : "";
  };

  private throwIfNotBrowser = () => {
    if (!isBrowser()) throw new Error("SSO-SDK: Not in browser.");
  };

  /**
   * get user from indexedDB without validating tokens.
   */
  readonly getLocalUser = async () => {
    const dbUser = (await this.db.getById(this.dbId)) as DBRes | undefined;

    if (!dbUser) throw new Error(`SSO-SDK: No user found in IndexedDB.`);

    return dbUser;
  };
}
